import { useCallback, useMemo, useState } from 'react'

type UseOrderedListOptions<T> = {
  items: T[]
  getId: (item: T) => string
  storageKey?: string
}

export function useOrderedList<T>({
                                    items,
                                    getId,
                                    storageKey,
                                  }: UseOrderedListOptions<T>) {
  // Initialize order from storage or fallback to current items
  const [order, setOrder] = useState<string[]>(() => {
    if (!storageKey) {
      return items.map(getId)
    }

    try {
      const saved = localStorage.getItem(storageKey)
      return saved ? JSON.parse(saved) : items.map(getId)
    } catch {
      return items.map(getId)
    }
  })

  // Build ordered list (no undefineds)
  const orderedItems = useMemo(() => {
    const itemById: Record<string, T> = {}

    for (const item of items) {
      itemById[getId(item)] = item
    }

    const result: T[] = []

    // Apply saved order
    for (const id of order) {
      if (itemById[id]) {
        result.push(itemById[id])
      }
    }

    // Append new items not in order
    for (const item of items) {
      const id = getId(item)
      if (!order.includes(id)) {
        result.push(item)
      }
    }

    return result
  }, [items, order, getId])

  // Move item (for drag & drop)
  const moveItem = useCallback(
    (fromIndex: number, toIndex: number) => {
      setOrder(prev => {
        const updated = [...prev]
        const [moved] = updated.splice(fromIndex, 1)
        updated.splice(toIndex, 0, moved)

        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(updated))
        }

        return updated
      })
    },
    [storageKey]
  )

  return {
    orderedItems,
    moveItem,
    setOrder, // optional manual control
  }
}